//frontend/dispatcher-client/src/hooks/usePersonnelDashboardData.js
import {
  useMemo,
} from "react";

import usePersonnelCalendarData from "./usePersonnelCalendarData";

const DAY_MS =
  24 *
  60 *
  60 *
  1000;

/* =========================================================
 * HELPERS
 * ======================================================= */

const getPersonKey = (
  personType,
  personId
) =>
  `${String(
    personType ||
      ""
  ).toUpperCase()}:${Number(
    personId
  )}`;

const startOfDay = (
  value
) => {
  const date =
    new Date(
      value
    );

  date.setHours(
    0,
    0,
    0,
    0
  );

  return date;
};

const endOfDay = (
  value
) => {
  const date =
    new Date(
      value
    );

  date.setHours(
    23,
    59,
    59,
    999
  );

  return date;
};

const localDateKey = (
  value
) => {
  const date =
    value instanceof Date
      ? value
      : new Date(
          value
        );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  return [
    date.getFullYear(),

    String(
      date.getMonth() +
        1
    ).padStart(
      2,
      "0"
    ),

    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    ),
  ].join(
    "-"
  );
};

const dateOnlyKey = (
  value
) => {
  const text =
    String(
      value ||
        ""
    ).slice(
      0,
      10
    );

  return /^\d{4}-\d{2}-\d{2}$/
    .test(
      text
    )
      ? text
      : null;
};

const parseDateOnly = (
  value
) => {
  const key =
    dateOnlyKey(
      value
    );

  if (!key) {
    return null;
  }

  const [
    year,
    month,
    day,
  ] =
    key
      .split(
        "-"
      )
      .map(
        Number
      );

  return new Date(
    year,
    month - 1,
    day
  );
};

const clampDate = (
  value,
  min,
  max
) => {
  if (
    value <
    min
  ) {
    return new Date(
      min
    );
  }

  if (
    value >
    max
  ) {
    return new Date(
      max
    );
  }

  return new Date(
    value
  );
};

const overlapsPeriod = (
  startValue,
  endValue,
  periodStart,
  periodEnd
) => {
  const start =
    new Date(
      startValue
    );

  const end =
    new Date(
      endValue
    );

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    )
  ) {
    return false;
  }

  return (
    start <=
      periodEnd &&
    end >=
      periodStart
  );
};

/* =========================================================
 * PERIOD
 * ======================================================= */

const getPeriod = ({
  year,
  month,
  mode,
}) => {
  const normalizedMode =
    mode ===
    "MONTH"
      ? "MONTH"
      : "YEAR";

  const start =
    normalizedMode ===
    "MONTH"
      ? new Date(
          year,
          month,
          1
        )
      : new Date(
          year,
          0,
          1
        );

  const end =
    normalizedMode ===
    "MONTH"
      ? new Date(
          year,
          month + 1,
          0,
          23,
          59,
          59,
          999
        )
      : new Date(
          year,
          11,
          31,
          23,
          59,
          59,
          999
        );

  return {
    mode:
      normalizedMode,

    start,

    end,

    startKey:
      localDateKey(
        start
      ),

    endKey:
      localDateKey(
        end
      ),
  };
};

/* =========================================================
 * INTERVAL -> DATE KEYS
 * ======================================================= */

const expandIntervalDays = ({
  startValue,
  endValue,
  periodStart,
  periodEnd,
}) => {
  const start =
    new Date(
      startValue
    );

  const end =
    new Date(
      endValue
    );

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    )
  ) {
    return [];
  }

  const first =
    startOfDay(
      clampDate(
        start,
        periodStart,
        periodEnd
      )
    );

  const last =
    startOfDay(
      clampDate(
        end,
        periodStart,
        periodEnd
      )
    );

  const result =
    [];

  for (
    let cursor =
      new Date(
        first
      );
    cursor <=
      last;
    cursor =
      new Date(
        cursor.getTime() +
          DAY_MS
      )
  ) {
    const dayStart =
      startOfDay(
        cursor
      );

    const dayEnd =
      endOfDay(
        cursor
      );

    if (
      start <=
        dayEnd &&
      end >=
        dayStart
    ) {
      const key =
        localDateKey(
          cursor
        );

      if (key) {
        result.push(
          key
        );
      }
    }
  }

  return result;
};

/* =========================================================
 * PERSON STATE
 * ======================================================= */

const createPersonState = (
  person
) => ({
  person,

  personKey:
    getPersonKey(
      person?.personType,
      person?.personId
    ),

  working:
    new Set(),

  off:
    new Set(),

  vacation:
    new Set(),

  absence:
    new Set(),

  holidayWork:
    new Set(),

  pendingRequests:
    0,

  nextAbsence:
    null,
});

const addHistoricalDate = (
  set,
  dateKey,
  historicalEndKey
) => {
  if (
    dateKey &&
    dateKey <=
      historicalEndKey
  ) {
    set.add(
      dateKey
    );
  }
};

const getHolidayWorkDateKey = (
  row
) =>
  dateOnlyKey(
    row
      ?.publicHoliday
      ?.date
  ) ||
  localDateKey(
    row?.startAt
  );

/* =========================================================
 * TIME SERIES
 * ======================================================= */

const buildTimeSeries = ({
  rows,
  year,
  month,
  mode,
  locale,
}) => {
  const series =
    [];

  /*
   * Monthly view:
   * group by weeks.
   */
  if (
    mode ===
    "MONTH"
  ) {
    const first =
      new Date(
        year,
        month,
        1
      );

    const mondayOffset =
      (
        first.getDay() +
        6
      ) %
      7;

    const daysInMonth =
      new Date(
        year,
        month + 1,
        0
      ).getDate();

    const weekCount =
      Math.ceil(
        (
          mondayOffset +
          daysInMonth
        ) /
          7
      );

    for (
      let index =
        0;
      index <
      weekCount;
      index +=
        1
    ) {
      series.push({
        key:
          `week-${index}`,

        label:
          `Sem. ${index + 1}`,

        working:
          0,

        vacation:
          0,

        absence:
          0,
      });
    }

    const getBucketIndex = (
      dateKey
    ) => {
      const date =
        parseDateOnly(
          dateKey
        );

      if (!date) {
        return -1;
      }

      return Math.floor(
        (
          date.getDate() -
          1 +
          mondayOffset
        ) /
          7
      );
    };

    rows.forEach(
      (
        row
      ) => {
        row
          .effectiveWorkingDates
          .forEach(
            (
              key
            ) => {
              const index =
                getBucketIndex(
                  key
                );

              if (
                series[
                  index
                ]
              ) {
                series[
                  index
                ].working +=
                  1;
              }
            }
          );

        row
          .vacationDates
          .forEach(
            (
              key
            ) => {
              const index =
                getBucketIndex(
                  key
                );

              if (
                series[
                  index
                ]
              ) {
                series[
                  index
                ].vacation +=
                  1;
              }
            }
          );

        row
          .absenceDates
          .forEach(
            (
              key
            ) => {
              const index =
                getBucketIndex(
                  key
                );

              if (
                series[
                  index
                ]
              ) {
                series[
                  index
                ].absence +=
                  1;
              }
            }
          );
      }
    );

    return series;
  }

  /*
   * Annual view:
   * group by month.
   */
  const monthFormatter =
    new Intl.DateTimeFormat(
      locale ||
        "pt-PT",
      {
        month:
          "short",
      }
    );

  for (
    let index =
      0;
    index <
    12;
    index +=
      1
  ) {
    series.push({
      key:
        `month-${index}`,

      label:
        monthFormatter
          .format(
            new Date(
              year,
              index,
              1
            )
          )
          .replace(
            ".",
            ""
          ),

      working:
        0,

      vacation:
        0,

      absence:
        0,
    });
  }

  const getBucketIndex = (
    dateKey
  ) => {
    const date =
      parseDateOnly(
        dateKey
      );

    return date
      ? date.getMonth()
      : -1;
  };

  rows.forEach(
    (
      row
    ) => {
      row
        .effectiveWorkingDates
        .forEach(
          (
            key
          ) => {
            const index =
              getBucketIndex(
                key
              );

            if (
              series[
                index
              ]
            ) {
              series[
                index
              ].working +=
                1;
            }
          }
        );

      row
        .vacationDates
        .forEach(
          (
            key
          ) => {
            const index =
              getBucketIndex(
                key
              );

            if (
              series[
                index
              ]
            ) {
              series[
                index
              ].vacation +=
                1;
            }
          }
        );

      row
        .absenceDates
        .forEach(
          (
            key
          ) => {
            const index =
              getBucketIndex(
                key
              );

            if (
              series[
                index
              ]
            ) {
              series[
                index
              ].absence +=
                1;
            }
          }
        );
    }
  );

  return series;
};

/* =========================================================
 * HOOK
 * ======================================================= */

export default function usePersonnelDashboardData({
  year,
  month,
  mode,
  locale,
}) {
  const calendarData =
    usePersonnelCalendarData(
      year
    );

  const derived =
    useMemo(
      () => {
        const {
          people = [],
          absences = [],
          holidayWork = [],
          workDays = [],
        } =
          calendarData;

        const period =
          getPeriod({
            year,
            month,
            mode,
          });

        const now =
          new Date();

        const todayEnd =
          endOfDay(
            now
          );

        /*
         * We only count finished/current dates.
         *
         * Future planned work must not count as
         * "worked" yet.
         */
        const hasHistoricalWindow =
          period.start <=
          todayEnd;

        const historicalEnd =
          hasHistoricalWindow
            ? new Date(
                Math.min(
                  period.end.getTime(),
                  todayEnd.getTime()
                )
              )
            : new Date(
                period.start.getTime() -
                  1
              );

        const historicalEndKey =
          hasHistoricalWindow
            ? localDateKey(
                historicalEnd
              )
            : "0000-00-00";

        /*
         * One state object per USER / DRIVER.
         */
        const states =
          new Map();

        people.forEach(
          (
            person
          ) => {
            const key =
              getPersonKey(
                person?.personType,
                person?.personId
              );

            states.set(
              key,
              createPersonState(
                person
              )
            );
          }
        );

        /* =================================================
         * WORK DAYS
         * =============================================== */

        workDays.forEach(
          (
            row
          ) => {
            const personKey =
              getPersonKey(
                row?.personType,
                row?.personId
              );

            const state =
              states.get(
                personKey
              );

            const workDate =
              dateOnlyKey(
                row?.workDate
              );

            if (
              !state ||
              !workDate ||
              workDate <
                period.startKey ||
              workDate >
                period.endKey ||
              workDate >
                historicalEndKey
            ) {
              return;
            }

            const status =
              String(
                row?.status ||
                  ""
              ).toUpperCase();

            if (
              status ===
              "WORKING"
            ) {
              state
                .working
                .add(
                  workDate
                );

              return;
            }

            if (
              status ===
              "OFF"
            ) {
              state
                .off
                .add(
                  workDate
                );
            }
          }
        );

        /* =================================================
         * ABSENCES
         * =============================================== */

        absences.forEach(
          (
            absence
          ) => {
            const personKey =
              getPersonKey(
                absence?.personType,
                absence?.personId
              );

            const state =
              states.get(
                personKey
              );

            if (!state) {
              return;
            }

            const status =
              String(
                absence?.status ||
                  ""
              ).toUpperCase();

            /*
             * Pending requests KPI.
             */
            if (
              status ===
                "PENDING" &&
              overlapsPeriod(
                absence?.startAt,
                absence?.endAt,
                period.start,
                period.end
              )
            ) {
              state.pendingRequests +=
                1;
            }

            /*
             * Upcoming absence.
             *
             * Both pending and approved are useful here,
             * because management wants to know what is
             * coming next.
             */
            if (
              [
                "PENDING",
                "APPROVED",
              ].includes(
                status
              ) &&
              overlapsPeriod(
                absence?.startAt,
                absence?.endAt,
                period.start,
                period.end
              )
            ) {
              const start =
                new Date(
                  absence?.startAt
                );

              if (
                !Number.isNaN(
                  start.getTime()
                ) &&
                start >=
                  startOfDay(
                    now
                  )
              ) {
                const currentNext =
                  state
                    .nextAbsence
                    ?.startAt
                    ? new Date(
                        state
                          .nextAbsence
                          .startAt
                      )
                    : null;

                if (
                  !currentNext ||
                  start <
                    currentNext
                ) {
                  state.nextAbsence =
                    absence;
                }
              }
            }

            /*
             * Only APPROVED absences affect historical
             * worked/vacation/absence calculations.
             */
            if (
              status !==
                "APPROVED" ||
              !hasHistoricalWindow ||
              !overlapsPeriod(
                absence?.startAt,
                absence?.endAt,
                period.start,
                historicalEnd
              )
            ) {
              return;
            }

            const dayKeys =
              expandIntervalDays({
                startValue:
                  absence?.startAt,

                endValue:
                  absence?.endAt,

                periodStart:
                  period.start,

                periodEnd:
                  historicalEnd,
              });

            const isVacation =
              String(
                absence?.absenceType ||
                  ""
              ).toUpperCase() ===
              "HOLIDAY";

            dayKeys.forEach(
              (
                dateKey
              ) => {
                addHistoricalDate(
                  isVacation
                    ? state
                        .vacation
                    : state
                        .absence,

                  dateKey,

                  historicalEndKey
                );
              }
            );
          }
        );

        /* =================================================
         * HOLIDAY WORK
         * =============================================== */

        holidayWork.forEach(
          (
            row
          ) => {
            const status =
              String(
                row?.status ||
                  ""
              ).toUpperCase();

            if (
              status !==
              "CONFIRMED"
            ) {
              return;
            }

            const personKey =
              getPersonKey(
                row?.personType,
                row?.personId
              );

            const state =
              states.get(
                personKey
              );

            const dateKey =
              getHolidayWorkDateKey(
                row
              );

            if (
              !state ||
              !dateKey ||
              dateKey <
                period.startKey ||
              dateKey >
                period.endKey ||
              dateKey >
                historicalEndKey
            ) {
              return;
            }

            state
              .holidayWork
              .add(
                dateKey
              );
          }
        );

        /* =================================================
         * PERSON ROWS
         * =============================================== */

        const rows =
          Array
            .from(
              states.values()
            )
            .map(
              (
                state
              ) => {
                /*
                 * Effective work:
                 *
                 * WORKING
                 * - approved vacation
                 * - approved absence
                 *
                 * plus confirmed holiday work.
                 */
                const effectiveWorkingDates =
                  new Set();

                state
                  .working
                  .forEach(
                    (
                      dateKey
                    ) => {
                      if (
                        !state
                          .vacation
                          .has(
                            dateKey
                          ) &&
                        !state
                          .absence
                          .has(
                            dateKey
                          )
                      ) {
                        effectiveWorkingDates
                          .add(
                            dateKey
                          );
                      }
                    }
                  );

                state
                  .holidayWork
                  .forEach(
                    (
                      dateKey
                    ) => {
                      if (
                        !state
                          .vacation
                          .has(
                            dateKey
                          ) &&
                        !state
                          .absence
                          .has(
                            dateKey
                          )
                      ) {
                        effectiveWorkingDates
                          .add(
                            dateKey
                          );
                      }
                    }
                  );

                return {
                  ...state,

                  effectiveWorkingDates,

                  vacationDates:
                    state
                      .vacation,

                  absenceDates:
                    state
                      .absence,

                  workedDays:
                    effectiveWorkingDates
                      .size,

                  vacationDays:
                    state
                      .vacation
                      .size,

                  absenceDays:
                    state
                      .absence
                      .size,

                  offDays:
                    state
                      .off
                      .size,

                  holidayWorkedDays:
                    state
                      .holidayWork
                      .size,
                };
              }
            );

        /*
         * Highest worked count first.
         */
        rows.sort(
          (
            a,
            b
          ) =>
            b.workedDays -
              a.workedDays ||
            String(
              a
                .person
                ?.displayName ||
                ""
            ).localeCompare(
              String(
                b
                  .person
                  ?.displayName ||
                  ""
              ),
              locale ||
                "pt-PT"
            )
        );

        /* =================================================
         * TOTALS
         * =============================================== */

        const totals =
          rows.reduce(
            (
              acc,
              row
            ) => {
              acc.workedDays +=
                row.workedDays;

              acc.vacationDays +=
                row.vacationDays;

              acc.absenceDays +=
                row.absenceDays;

              acc.offDays +=
                row.offDays;

              acc.holidayWorkedDays +=
                row.holidayWorkedDays;

              acc.pendingRequests +=
                row.pendingRequests;

              return acc;
            },
            {
              workedDays:
                0,

              vacationDays:
                0,

              absenceDays:
                0,

              offDays:
                0,

              holidayWorkedDays:
                0,

              pendingRequests:
                0,
            }
          );

        /* =================================================
         * DISTRIBUTION
         *
         * Each person/date should only belong to one
         * visible state.
         *
         * Priority:
         *
         * vacation
         * absence
         * holiday work
         * working
         * off
         * =============================================== */

        const distributionCounts = {
          working:
            0,

          vacation:
            0,

          absence:
            0,

          off:
            0,

          holidayWork:
            0,
        };

        rows.forEach(
          (
            row
          ) => {
            const allDates =
              new Set([
                ...row
                  .working,

                ...row
                  .off,

                ...row
                  .vacation,

                ...row
                  .absence,

                ...row
                  .holidayWork,
              ]);

            allDates.forEach(
              (
                dateKey
              ) => {
                if (
                  row
                    .vacation
                    .has(
                      dateKey
                    )
                ) {
                  distributionCounts
                    .vacation +=
                    1;

                  return;
                }

                if (
                  row
                    .absence
                    .has(
                      dateKey
                    )
                ) {
                  distributionCounts
                    .absence +=
                    1;

                  return;
                }

                if (
                  row
                    .holidayWork
                    .has(
                      dateKey
                    )
                ) {
                  distributionCounts
                    .holidayWork +=
                    1;

                  return;
                }

                if (
                  row
                    .working
                    .has(
                      dateKey
                    )
                ) {
                  distributionCounts
                    .working +=
                    1;

                  return;
                }

                if (
                  row
                    .off
                    .has(
                      dateKey
                    )
                ) {
                  distributionCounts
                    .off +=
                    1;
                }
              }
            );
          }
        );

        const distributionTotal =
          Object
            .values(
              distributionCounts
            )
            .reduce(
              (
                total,
                value
              ) =>
                total +
                value,
              0
            );

        const distribution =
          Object
            .entries(
              distributionCounts
            )
            .map(
              ([
                key,
                count,
              ]) => ({
                key,

                count,

                percentage:
                  distributionTotal >
                  0
                    ? count /
                      distributionTotal
                    : 0,
              })
            );

        /* =================================================
         * TIME SERIES
         * =============================================== */

        const timeSeries =
          buildTimeSeries({
            rows,

            year,

            month,

            mode:
              period.mode,

            locale,
          });

        /* =================================================
         * INSIGHTS
         * =============================================== */

        const topWorker =
          rows.find(
            (
              row
            ) =>
              row.workedDays >
              0
          ) ||
          null;

        const topVacation =
          [...rows]
            .sort(
              (
                a,
                b
              ) =>
                b.vacationDays -
                a.vacationDays
            )
            .find(
              (
                row
              ) =>
                row.vacationDays >
                0
            ) ||
          null;

        const topAbsence =
          [...rows]
            .sort(
              (
                a,
                b
              ) =>
                b.absenceDays -
                a.absenceDays
            )
            .find(
              (
                row
              ) =>
                row.absenceDays >
                0
            ) ||
          null;

        const topOff =
          [...rows]
            .sort(
              (
                a,
                b
              ) =>
                b.offDays -
                a.offDays
            )
            .find(
              (
                row
              ) =>
                row.offDays >
                0
            ) ||
          null;

        const mostPending =
          [...rows]
            .sort(
              (
                a,
                b
              ) =>
                b.pendingRequests -
                a.pendingRequests
            )
            .find(
              (
                row
              ) =>
                row.pendingRequests >
                0
            ) ||
          null;

        const holidayWorkerCount =
          rows.filter(
            (
              row
            ) =>
              row.holidayWorkedDays >
              0
          ).length;

        const peakVacationBucket =
          [...timeSeries]
            .sort(
              (
                a,
                b
              ) =>
                b.vacation -
                a.vacation
            )[0] ||
          null;

        return {
          period,
          historicalEnd,
          hasHistoricalWindow,

          rows,
          totals,

          distribution,
          distributionTotal,

          timeSeries,

          insights: {
            topWorker,
            topVacation,
            topAbsence,
            topOff,
            mostPending,
            holidayWorkerCount,

            peakVacationBucket:
              peakVacationBucket
                ?.vacation >
              0
                ? peakVacationBucket
                : null,
          },
        };
      },
      [
        calendarData
          .people,

        calendarData
          .absences,

        calendarData
          .holidayWork,

        calendarData
          .workDays,

        year,

        month,

        mode,

        locale,
      ]
    );

  return {
    ...calendarData,

    ...derived,
  };
}